import React, { useCallback } from "react";
import { DragDropContext, Draggable, Droppable, type DropResult } from "react-beautiful-dnd";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { Moment } from "moment";

import type { TeamType, UserType } from "~/common/AssigneeFilter";
import KeyResult from "~/okrs/KeyResult";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import { Button } from "~/ui";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import type { SelectorOption } from "~/ui/selectors/Selector";

import type { MetricProgress, MetricType } from "~/__generated__/ObjectiveQuery_Info.graphql";

import styles from "./EditableKeyResultList.module.css";

interface EditableKeyResultListProps {
	objective: ObjectiveAttributes;
	hasAccess: boolean;
	loadingCreation?: boolean;
	className?: string;
	onCreate(): void;
	onTitleChange(newTitle: string, index: number): void;
	onDueDateChange(newDueDate: Moment | null, index: number): void;
	onAssigneeChange(user: UserType, index: number): void;
	onAssigneeTeamChange(team: TeamType, index: number): void;
	onMetricChange(
		newMetricProgress: MetricProgress,
		newMetricType: MetricType,
		newMetricCurrency: string | undefined,
		newMetricStart: number,
		newMetricCurrent: number,
		newMetricTarget: number,
		index: number,
	): void;
	onWeightChange(newWeight: number, index: number): void;
	onOrderChange({ destination, source }: DropResult): void;
	onDelete(index: number): void;
	setIsAddingDescription?: (isAddingDescription: boolean) => void;
	setSelectedOkr?: (selectedOkr: SelectorOption) => void;
	onChangeToUpdatesView?: () => void;
}

export default function EditableKeyResultList({
	objective,
	hasAccess,
	loadingCreation,
	className,
	onCreate,
	onTitleChange,
	onDueDateChange,
	onAssigneeChange,
	onAssigneeTeamChange,
	onMetricChange,
	onWeightChange,
	onOrderChange,
	onDelete,
	setIsAddingDescription,
	setSelectedOkr,
	onChangeToUpdatesView,
}: EditableKeyResultListProps) {
	// Defined here so it can be reused as Droppable's renderClone prop - see:
	// https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md#cloning-api
	const draggableInnerRenderer = useCallback(
		(provided, snapshot, rubric) => {
			const keyResult = objective.keyResults[rubric.source.index];
			return (
				<KeyResult
					{...provided.draggableProps}
					{...provided.dragHandleProps}
					{...keyResult}
					className={cx({ [styles.dragging]: snapshot.isDragging })}
					innerRef={provided.innerRef}
					objectiveId={objective.id}
					archived={false}
					hasAccess={hasAccess}
					/* eslint-disable-next-line react/jsx-no-bind */
					onTitleChange={(newTitle: string) => onTitleChange(newTitle, rubric.source.index)}
					/* eslint-disable-next-line react/jsx-no-bind */
					onDueDateChange={(newDueDate: Moment) => onDueDateChange(newDueDate, rubric.source.index)}
					/* eslint-disable-next-line react/jsx-no-bind */
					onAssigneeChange={(assignee: UserType) => onAssigneeChange(assignee, rubric.source.index)}
					/* eslint-disable-next-line react/jsx-no-bind */
					onAssigneeTeamChange={(assignee: TeamType) => onAssigneeTeamChange(assignee, rubric.source.index)}
					/* eslint-disable-next-line react/jsx-no-bind */
					onMetricChange={(
						newMetricProgress: MetricProgress,
						newMetricType: MetricType,
						newMetricCurrency: string | undefined,
						newMetricStart: number,
						newMetricCurrent: number,
						newMetricTarget: number,
					) =>
						onMetricChange(
							newMetricProgress,
							newMetricType,
							newMetricCurrency,
							newMetricStart,
							newMetricCurrent,
							newMetricTarget,
							rubric.source.index,
						)
					}
					/* eslint-disable-next-line react/jsx-no-bind */
					onWeightChange={(newWeight: number) => onWeightChange(newWeight, rubric.source.index)}
					/* eslint-disable-next-line react/jsx-no-bind */
					onDelete={() => onDelete(rubric.source.index)}
					setIsAddingDescription={setIsAddingDescription}
					setSelectedOkr={setSelectedOkr}
					onChangeToUpdatesView={onChangeToUpdatesView}
				/>
			);
		},
		[
			objective.id,
			objective.keyResults,
			onTitleChange,
			onDueDateChange,
			onAssigneeChange,
			onAssigneeTeamChange,
			onMetricChange,
			onWeightChange,
			onDelete,
			hasAccess,
			setIsAddingDescription,
			setSelectedOkr,
			onChangeToUpdatesView,
		],
	);

	return (
		<div className={cx(styles.keyResultList, className)}>
			{hasAccess ? (
				<DragDropContext onDragEnd={onOrderChange}>
					<Droppable droppableId="key-results" renderClone={draggableInnerRenderer}>
						{provided => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								{objective.keyResults.map((keyResult, index) => (
									<Draggable
										key={keyResult.id}
										draggableId={keyResult.id ? keyResult.id : index.toString()}
										index={index}
									>
										{draggableInnerRenderer}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			) : (
				objective.keyResults.map((keyResult, index) => (
					<KeyResult
						{...keyResult}
						key={`key-result-${keyResult.id}`}
						objectiveId={objective.id}
						archived={false}
						hasAccess={hasAccess}
						/* eslint-disable-next-line react/jsx-no-bind */
						onTitleChange={(newTitle: string) => onTitleChange(newTitle, index)}
						/* eslint-disable-next-line react/jsx-no-bind */
						onDueDateChange={(newDueDate: Moment | null) => onDueDateChange(newDueDate, index)}
						/* eslint-disable-next-line react/jsx-no-bind */
						onAssigneeChange={(assignee: UserType) => onAssigneeChange(assignee, index)}
						/* eslint-disable-next-line react/jsx-no-bind */
						onAssigneeTeamChange={(assignee: TeamType) => onAssigneeTeamChange(assignee, index)}
						/* eslint-disable-next-line react/jsx-no-bind */
						onMetricChange={(
							newMetricProgress: MetricProgress,
							newMetricType: MetricType,
							newMetricCurrency: string | undefined,
							newMetricStart: number,
							newMetricCurrent: number,
							newMetricTarget: number,
						) =>
							onMetricChange(
								newMetricProgress,
								newMetricType,
								newMetricCurrency,
								newMetricStart,
								newMetricCurrent,
								newMetricTarget,
								index,
							)
						}
						/* eslint-disable-next-line react/jsx-no-bind */
						onWeightChange={(newWeight: number) => onWeightChange(newWeight, index)}
						/* eslint-disable-next-line react/jsx-no-bind */
						onDelete={() => onDelete(index)}
						setIsAddingDescription={setIsAddingDescription}
						setSelectedOkr={setSelectedOkr}
					/>
				))
			)}
			{hasAccess &&
				(loadingCreation ? (
					<LoadingSpinner inline className={styles.creationLoading} />
				) : (
					<Button size="wide" className={styles.createKeyResult} onClick={onCreate}>
						<span>
							<FontAwesomeIcon icon={faPlus} />
						</span>
						Create key result
					</Button>
				))}
		</div>
	);
}
